import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * The single definition of what a status looks like.
 *
 * The skill's rule is that a status never changes colour meaning between
 * screens, which only holds if there is one mapping. Screens pass a `tone`,
 * never a colour, and new statuses are added HERE — not restyled per screen.
 *
 *   success     completed · active · paid
 *   warning     pending · expiring
 *   destructive failed · cancelled · overdue
 *   neutral     inactive · draft
 *   info        in-progress / informational, carried in brand teal
 */
export type StatusTone = "success" | "warning" | "destructive" | "neutral" | "info";

const TONE_TO_VARIANT: Record<StatusTone, BadgeVariant> = {
  success: "success",
  warning: "warning",
  destructive: "destructive",
  neutral: "muted",
  info: "primary",
};

const TONE_TO_DOT: Record<StatusTone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  destructive: "bg-destructive",
  neutral: "bg-muted-foreground",
  info: "bg-primary",
};

interface StatusBadgeProps {
  tone: StatusTone;
  /** Always visible. Status is never conveyed by colour alone. */
  label: string;
  className?: string;
}

/**
 * The same tone as a dot beside plain text, with no fill — for a row's
 * SECONDARY state. A row carries at most one badge (its status); a second
 * fact about it in another pill makes every row two loud shapes and nothing
 * stands out. Same tone map, so the colour still means the same thing.
 */
export function StatusText({ tone, label, className }: StatusBadgeProps) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap", className)}>
      <span
        className={cn("size-1.5 shrink-0 rounded-full", TONE_TO_DOT[tone])}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/**
 * The dot alone, for placing inside a control — a select showing a status as
 * its current value. Same tone map as the badge, so the colour means the same.
 * Decorative: the word beside it carries the status.
 */
export function StatusDot({ tone, className }: { tone: StatusTone; className?: string }) {
  return (
    <span
      className={cn("size-1.5 shrink-0 rounded-full", TONE_TO_DOT[tone], className)}
      aria-hidden="true"
    />
  );
}

export function StatusBadge({ tone, label, className }: StatusBadgeProps) {
  return (
    <Badge
      variant={TONE_TO_VARIANT[tone]}
      // The label renders in the normal text colour, not the status colour.
      // Measured: status hues as text on their own 10% tint run 2.2:1 (warning)
      // to 4.6:1 — mostly under the 4.5:1 the design system calls
      // non-negotiable. Darkening the tokens enough to fix that would turn the
      // amber brown and collapse the status palette into muddy near-blacks.
      // So colour is carried by the tinted fill and the dot, and the word
      // itself stays at ~15:1. This is also literally what the skill asks for:
      // "coloured dot + label".
      className={cn("gap-1.5 text-foreground", className)}
    >
      <span
        className={cn("size-1.5 shrink-0 rounded-full", TONE_TO_DOT[tone])}
        aria-hidden="true"
      />
      {label}
    </Badge>
  );
}
